/**
 * CSV インポートのモーダルフロー (ISSUE-823)。
 *
 * 3 ステージ:
 *   1. FileSelect — ファイル選択 + テンプレート CSV DL リンク
 *   2. Preview    — 行ごとに重複検出結果を色分け表示し、行ごとの decision を選択可能
 *   3. Result     — 実行サマリー表示
 */
import { Button } from "components/ui/Button";
import {
  type CsvImportExecuteResponse,
  type CsvImportExecuteRow,
  type CsvImportPreviewResponse,
  type CsvImportPreviewRow,
  type CsvImportRowDecision,
  type CsvImportRowStatus,
  useCompanyCsv,
} from "hooks/useCompaniesApi";
import { FileText, Upload } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { toastError } from "utils/toast-utils";

type Stage = "file" | "preview" | "result";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCompleted?: () => void;
};

const STATUS_LABEL: Record<CsvImportRowStatus, string> = {
  NEW: "新規企業 + 連絡先",
  LINK_DOMAIN: "既存企業に連絡先追加",
  MERGE_CANDIDATE: "Merge 候補 (要確認)",
  COMPANY_ONLY_NEW: "新規企業のみ",
  COMPANY_ONLY_LINK: "既存企業 (操作なし)",
  SKIP_DUPLICATE_EMAIL: "email 重複 (スキップ)",
  ERROR: "エラー",
};

const STATUS_BADGE_CLASS: Record<CsvImportRowStatus, string> = {
  NEW: "badge badge-success",
  LINK_DOMAIN: "badge badge-info",
  MERGE_CANDIDATE: "badge badge-warning",
  COMPANY_ONLY_NEW: "badge badge-success",
  COMPANY_ONLY_LINK: "badge badge-ghost",
  SKIP_DUPLICATE_EMAIL: "badge badge-ghost",
  ERROR: "badge badge-error",
};

/**
 * 行ごとに選択可能な decision を返す。
 * @param {CsvImportRowStatus} status 行の判定状態
 * @return {CsvImportRowDecision[]} 選択肢
 */
function availableDecisions(
  status: CsvImportRowStatus,
): CsvImportRowDecision[] {
  switch (status) {
    case "MERGE_CANDIDATE":
      return ["ACCEPT", "CREATE_NEW", "SKIP"];
    case "NEW":
    case "LINK_DOMAIN":
    case "COMPANY_ONLY_NEW":
    case "COMPANY_ONLY_LINK":
      return ["ACCEPT", "SKIP"];
    case "SKIP_DUPLICATE_EMAIL":
    case "ERROR":
      return ["SKIP"];
    default:
      return ["ACCEPT"];
  }
}

/**
 * 行ごとの decision ラベル。
 * @param {CsvImportRowDecision} d decision
 * @return {string} 表示ラベル
 */
function decisionLabel(d: CsvImportRowDecision): string {
  switch (d) {
    case "ACCEPT":
      return "承認";
    case "CREATE_NEW":
      return "新規作成";
    case "SKIP":
      return "スキップ";
  }
}

/**
 * 企業マスタの CSV インポートフロー (3 ステージモーダル)。
 * @param {Props} props isOpen, onClose, onCompleted
 * @return {JSX.Element|null} モーダル
 */
export function CsvImportFlow({
  isOpen,
  onClose,
  onCompleted,
}: Props): JSX.Element | null {
  const { previewImport, executeImport, loading } = useCompanyCsv();
  const [stage, setStage] = useState<Stage>("file");
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState<CsvImportPreviewResponse | null>(null);
  const [decisions, setDecisions] = useState<
    Record<number, CsvImportRowDecision>
  >({});
  const [result, setResult] = useState<CsvImportExecuteResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) {
      setStage("file");
      setFile(null);
      setIsDragging(false);
      setPreview(null);
      setDecisions({});
      setResult(null);
    }
  }, [isOpen]);

  const handleFileDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (!dropped) return;
    if (!dropped.name.toLowerCase().endsWith(".csv")) {
      toastError("CSV ファイル (.csv) のみアップロードできます");
      return;
    }
    setFile(dropped);
  }, []);

  const handleUpload = useCallback(async () => {
    if (!file) {
      toastError("ファイルを選択してください");
      return;
    }
    try {
      const res = await previewImport(file);
      setPreview(res);
      // デフォルト: 全行 ACCEPT (SKIP_DUPLICATE_EMAIL / ERROR は SKIP)
      const initial: Record<number, CsvImportRowDecision> = {};
      for (const row of res.rows) {
        initial[row.rowNumber] =
          row.status === "SKIP_DUPLICATE_EMAIL" || row.status === "ERROR"
            ? "SKIP"
            : "ACCEPT";
      }
      setDecisions(initial);
      setStage("preview");
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toastError(`プレビューに失敗しました: ${msg}`);
    }
  }, [file, previewImport]);

  const handleExecute = useCallback(async () => {
    if (!preview) return;
    const rows: CsvImportExecuteRow[] = preview.rows
      .filter(r => r.status !== "ERROR")
      .map(r => ({
        rowNumber: r.rowNumber,
        input: r.input,
        status: r.status,
        existingCompanyId: r.existingCompanyId,
        decision: decisions[r.rowNumber] || "ACCEPT",
      }));
    if (rows.length === 0) {
      toastError("実行できる行がありません");
      return;
    }
    try {
      const res = await executeImport(rows);
      setResult(res);
      setStage("result");
      const created =
        res.summary.createdCompanies + res.summary.linkedToExisting;
      toast.success(
        `インポート完了: ${created} 件処理 / ${res.summary.errors} 件エラー`,
      );
      onCompleted?.();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toastError(`インポート実行に失敗しました: ${msg}`);
    }
  }, [preview, decisions, executeImport, onCompleted]);

  if (!isOpen) return null;

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-5xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-lg">
            CSV インポート
            {stage === "preview" && " — プレビュー"}
            {stage === "result" && " — 結果"}
          </h3>
          <button
            type="button"
            className="btn btn-sm btn-circle btn-ghost"
            onClick={onClose}
            aria-label="閉じる"
          >
            ✕
          </button>
        </div>

        {stage === "file" && (
          <div className="space-y-4">
            <p className="text-sm">
              企業マスタを CSV から一括取り込みします。
              フォーマットは下記テンプレートを参考にしてください (UTF-8, BOM
              付き)。
            </p>
            <a
              href="/companies_import_template.csv"
              download
              className="link link-primary text-sm"
            >
              テンプレート CSV をダウンロード
            </a>

            <div
              onDrop={handleFileDrop}
              onDragOver={e => {
                e.preventDefault();
                if (!isDragging) setIsDragging(true);
              }}
              onDragLeave={e => {
                e.preventDefault();
                setIsDragging(false);
              }}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              role="button"
              tabIndex={0}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragging
                  ? "border-primary bg-primary/10"
                  : "border-base-300 hover:border-base-content/40"
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={e => {
                  const picked = e.target.files?.[0];
                  if (picked) setFile(picked);
                  // 同じファイル再選択時にも onChange を発火させる
                  e.target.value = "";
                }}
              />
              {file ? (
                <div className="flex items-center justify-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  <span className="font-medium">{file.name}</span>
                  <span className="text-xs text-base-content/60">
                    ({(file.size / 1024).toFixed(1)} KB)
                  </span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 text-base-content/70">
                  <Upload className="w-8 h-8" />
                  <p className="text-sm">
                    CSV ファイルをドラッグ＆ドロップ
                    <br />
                    または
                    <span className="link link-primary mx-1">
                      クリックして選択
                    </span>
                  </p>
                </div>
              )}
            </div>

            <div className="modal-action">
              <Button variant="outline" size="sm" onClick={onClose}>
                キャンセル
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleUpload}
                disabled={!file || loading}
              >
                {loading ? "解析中..." : "プレビュー"}
              </Button>
            </div>
          </div>
        )}

        {stage === "preview" && preview && (
          <div className="space-y-3">
            <div className="grid grid-cols-7 gap-2 text-xs">
              <SummaryCell label="合計" value={preview.summary.total} />
              <SummaryCell label="新規" value={preview.summary.new} />
              <SummaryCell
                label="既存連絡先追加"
                value={preview.summary.linkDomain}
              />
              <SummaryCell
                label="Merge候補"
                value={preview.summary.mergeCandidate}
              />
              <SummaryCell
                label="企業のみ"
                value={
                  preview.summary.companyOnlyNew +
                  preview.summary.companyOnlyLink
                }
              />
              <SummaryCell
                label="email重複"
                value={preview.summary.skipDuplicateEmail}
              />
              <SummaryCell label="エラー" value={preview.summary.error} />
            </div>

            <div className="overflow-auto max-h-[60vh] border rounded">
              <table className="table table-xs">
                <thead>
                  <tr>
                    <th>行</th>
                    <th>状態</th>
                    <th>企業</th>
                    <th>email</th>
                    <th>名前</th>
                    <th>opted_out</th>
                    <th>判断</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map(row => (
                    <PreviewRow
                      key={row.rowNumber}
                      row={row}
                      decision={decisions[row.rowNumber] || "ACCEPT"}
                      onChangeDecision={d =>
                        setDecisions(prev => ({
                          ...prev,
                          [row.rowNumber]: d,
                        }))
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <div className="modal-action">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStage("file")}
              >
                戻る
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleExecute}
                disabled={loading}
              >
                {loading ? "実行中..." : "実行"}
              </Button>
            </div>
          </div>
        )}

        {stage === "result" && result && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2 text-sm">
              <SummaryCell
                label="新規企業作成"
                value={result.summary.createdCompanies}
              />
              <SummaryCell
                label="既存企業に紐付け"
                value={result.summary.linkedToExisting}
              />
              <SummaryCell
                label="連絡先作成"
                value={result.summary.createdContacts}
              />
              <SummaryCell label="スキップ" value={result.summary.skipped} />
              <SummaryCell label="エラー" value={result.summary.errors} />
              <SummaryCell label="合計" value={result.summary.total} />
            </div>
            {result.errors.length > 0 && (
              <div>
                <h4 className="font-semibold text-sm mb-1">
                  エラー詳細 ({result.errors.length} 件)
                </h4>
                <ul className="text-xs space-y-1 max-h-40 overflow-auto">
                  {result.errors.map(err => (
                    <li key={err.rowNumber} className="text-error">
                      行 {err.rowNumber}: {err.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="modal-action">
              <Button variant="default" size="sm" onClick={onClose}>
                閉じる
              </Button>
            </div>
          </div>
        )}
      </div>
      <div className="modal-backdrop" onClick={onClose} />
    </div>
  );
}

type SummaryCellProps = {
  label: string;
  value: number;
};

/**
 * サマリー表示用の 1 セル。
 * @param {SummaryCellProps} props label, value
 * @return {JSX.Element} セル
 */
function SummaryCell({ label, value }: SummaryCellProps): JSX.Element {
  return (
    <div className="border rounded p-2 text-center">
      <div className="text-xs text-base-content/60">{label}</div>
      <div className="font-bold text-lg">{value}</div>
    </div>
  );
}

type PreviewRowProps = {
  row: CsvImportPreviewRow;
  decision: CsvImportRowDecision;
  onChangeDecision: (d: CsvImportRowDecision) => void;
};

/**
 * プレビューテーブルの 1 行。
 * @param {PreviewRowProps} props row, decision, onChangeDecision
 * @return {JSX.Element} テーブル行
 */
function PreviewRow({
  row,
  decision,
  onChangeDecision,
}: PreviewRowProps): JSX.Element {
  const choices = availableDecisions(row.status);
  return (
    <tr>
      <td>{row.rowNumber}</td>
      <td>
        <span className={STATUS_BADGE_CLASS[row.status]}>
          {STATUS_LABEL[row.status]}
        </span>
        {row.existingCompanyName && (
          <div className="text-xs text-base-content/60 mt-1">
            既存: {row.existingCompanyName}
          </div>
        )}
        {row.errorMessage && (
          <div className="text-xs text-error mt-1">{row.errorMessage}</div>
        )}
      </td>
      <td>
        {row.input.companyName}
        {row.input.primaryDomain && (
          <div className="text-xs text-base-content/60">
            {row.input.primaryDomain}
          </div>
        )}
      </td>
      <td>{row.input.email || "—"}</td>
      <td>{row.input.contactName || "—"}</td>
      <td>{row.input.optedOut ? "true" : "false"}</td>
      <td>
        <select
          className="select select-bordered select-xs"
          value={decision}
          onChange={e =>
            onChangeDecision(e.target.value as CsvImportRowDecision)
          }
          disabled={choices.length === 1}
        >
          {choices.map(c => (
            <option key={c} value={c}>
              {decisionLabel(c)}
            </option>
          ))}
        </select>
      </td>
    </tr>
  );
}
